import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getOwnBusiness } from '../api/endpoints/businesses';
import { updateSchedule } from '../api/endpoints/schedule';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { BusinessDetail, ScheduleDay } from '../api/schemas';
import { useSession } from '../auth/useSession';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { WEEKDAYS, WEEKDAY_LABEL, hoursToScheduleDays, toHourClock } from '../lib/schedule';

const HOUR_OPTIONS = Array.from({ length: 25 }, (_unused, hour) => toHourClock(hour));

export function AgendaPage() {
  const { session } = useSession();
  const businessId = session?.businessId ?? '';
  const queryClient = useQueryClient();
  const idempotencyKey = useRef(createIdempotencyKeyHolder()).current;

  const businessQuery = useQuery({
    queryKey: ['business-detail', businessId],
    queryFn: getOwnBusiness,
    enabled: Boolean(businessId),
  });

  const [rows, setRows] = useState<ScheduleDay[] | undefined>();

  useEffect(() => {
    if (businessQuery.data) setRows(hoursToScheduleDays(businessQuery.data.hours));
  }, [businessQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (hours: ScheduleDay[]) =>
      updateSchedule({ businessId, hours }, idempotencyKey.ensure()),
    onSuccess: (business) => {
      queryClient.setQueryData<BusinessDetail | undefined>(
        ['business-detail', businessId],
        (previous) => (previous ? { ...previous, ...business } : previous),
      );
    },
    onSettled: () => idempotencyKey.reset(),
  });

  function updateRow(dayOfWeek: number, patch: Partial<ScheduleDay>) {
    setRows((current) =>
      current?.map((row) => (row.dayOfWeek === dayOfWeek ? { ...row, ...patch } : row)),
    );
  }

  return (
    <main>
      <PageHeader title="Horario" description="El horario semanal de tu negocio." />
      {businessQuery.isLoading ? <LoadingState label="Cargando horario…" /> : null}
      {businessQuery.isError ? <ErrorBanner message={errorMessage(businessQuery.error)} /> : null}
      {saveMutation.isError ? <ErrorBanner message={errorMessage(saveMutation.error)} /> : null}
      {saveMutation.isSuccess ? (
        <p role="status" className="notice">
          Horario guardado.
        </p>
      ) : null}
      {rows ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            saveMutation.mutate(rows);
          }}
        >
          <div className="table-scroll">
            <table className="agenda-table">
            <tbody>
              {WEEKDAYS.map((dayOfWeek) => {
                const row = rows.find((item) => item.dayOfWeek === dayOfWeek);
                if (!row) return null;
                return (
                  <tr key={dayOfWeek}>
                    <th scope="row">{WEEKDAY_LABEL[dayOfWeek]}</th>
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          checked={Boolean(row.isClosed)}
                          onChange={(event) => updateRow(dayOfWeek, { isClosed: event.target.checked })}
                        />
                        Cerrado
                      </label>
                    </td>
                    <td>
                      <label htmlFor={`abre-${dayOfWeek}`} className="sr-only">
                        Abre
                      </label>
                      <select
                        id={`abre-${dayOfWeek}`}
                        value={row.opensAt ?? ''}
                        disabled={row.isClosed}
                        onChange={(event) => updateRow(dayOfWeek, { opensAt: event.target.value })}
                      >
                        <option value="" disabled>
                          Abre
                        </option>
                        {HOUR_OPTIONS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <label htmlFor={`cierra-${dayOfWeek}`} className="sr-only">
                        Cierra
                      </label>
                      <select
                        id={`cierra-${dayOfWeek}`}
                        value={row.closesAt ?? ''}
                        disabled={row.isClosed}
                        onChange={(event) => updateRow(dayOfWeek, { closesAt: event.target.value })}
                      >
                        <option value="" disabled>
                          Cierra
                        </option>
                        {HOUR_OPTIONS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
          <button type="submit" className="btn" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Guardando…' : 'Guardar horario'}
          </button>
        </form>
      ) : null}
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos guardar el horario.';
}
